import { Component, ElementRef, OnInit, Renderer2 } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { NavbarService } from '../services/navbar.service'
import { trigger, transition, style, animate, state, AnimationBuilder } from '@angular/animations'
import { LoadingService } from '../services/loading.service'
import { HttpClient } from '@angular/common/http'

@Component({
  selector: 'app-intro',
  templateUrl: './intro.component.html',
  styleUrls: ['./intro.component.css'],
  animations: [
    trigger('income', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('1500ms', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('1500ms', style({ opacity: 0 }))
      ])
    ]),
    trigger('scaleInOut', [
      state('initial', style({
        transform: 'scale(1)'
      })),
      state('final', style({
        transform: 'scale(1.5)'
      })),
      transition('initial=>final', animate('300ms')),
      transition('final=>initial', animate('200ms'))
    ]),
    trigger('slideInRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('300ms ease-out', style({ transform: 'translateX(0%)' }))
      ])
    ]),
    trigger('slideInTop', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)' }),
        animate('300ms ease-out', style({ transform: 'translateY(0%)' }))
      ])
    ]),
    trigger('fadeIn', [
      state('in', style({
        transform: 'scale(1)'
      })),
    ]),
    trigger('myAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('1s ease-out', style({ opacity: 1 }))
      ])
    ])
  ]
})
export class IntroComponent implements OnInit {
  sectionId: string
  register: boolean = true
  elementInViewport = false
  public observer: IntersectionObserver
  isLoading = false

  constructor(public navbarService: NavbarService,
    private route: ActivatedRoute,
    private renderer: Renderer2,
    public elementRef: ElementRef,
    private animationBuilder: AnimationBuilder,
    private router: Router,
    private http: HttpClient,
    private loadingService: LoadingService) { }

  ngOnInit(): void {
    this.isLoading = true
    this.loadingService.showLoader()
    this.navbarService.showNavbar = true

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5 // change this to adjust when the element is considered "in view"
    }

    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.renderer.addClass(this.elementRef.nativeElement, 'in-view')
          this.animateIntro()
        } else {
          this.renderer.removeClass(this.elementRef.nativeElement, 'in-view')
        }
      })
    }, options)

    this.observer.observe(this.elementRef.nativeElement)
    this.isLoading = false
    this.loadingService.hideLoader()
  }

  animateIntro() {
    const intro = this.elementRef.nativeElement
    const animation = this.animationBuilder.build([
      style({ opacity: 0, transform: 'translateY(100px)' }),
      animate('1s ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
    ])

    animation.create(intro).play()
  }

  onInView({ inView, element }: { inView: boolean, element: ElementRef }) {
    if (inView) {
      const anim = element.nativeElement
      const animation = this.animationBuilder.build([
        style({ opacity: 0, transform: 'translateY(100px)' }),
        animate('1s ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
      // Check if the animation has already been played
      if (!anim.classList.contains('played')) {
        anim.classList.add('played') // Add a class to mark the element as played
        animation.create(anim).play()
      }
    }
  }

  slideFromRight({ inView, element }: { inView: boolean, element: ElementRef }) {
    if (inView) {
      const anim = element.nativeElement
      const animation = this.animationBuilder.build([
        style({ transform: 'translateX(100%)' }),
        animate('1s ease-out', style({ transform: 'translateX(0%)' }))
      ]);
      // Check if the animation has already been played
      if (!anim.classList.contains('played')) {
        anim.classList.add('played') // Add a class to mark the element as played
        animation.create(anim).play()
      }
    }
  }

  slideFromLeft({ inView, element }: { inView: boolean, element: ElementRef }) {
    const anim = element.nativeElement

    if (inView) {
      const animation = this.animationBuilder.build([
        style({ transform: 'translateX(-100%)' }),
        animate('1s ease-in-out', style({ transform: 'translateX(0%)' }))
      ]);

      // Check if the animation has already been played
      if (!anim.classList.contains('played')) {
        anim.classList.add('played') // Add a class to mark the element as played
        animation.create(anim).play()
      }
    }
  }

  fadeIn({ inView, element }: { inView: boolean, element: ElementRef }) {
    const anim = element.nativeElement;

    if (inView) {
      const animation = this.animationBuilder.build([
        style({ opacity: 0 }),
        animate('1s ease-out', style({ opacity: 1 }))
      ])

      // Check if the animation has already been played
      if (!anim.classList.contains('played')) {
        anim.classList.add('played') // Add a class to mark the element as played
        animation.create(anim).play()
      }
    }
  }

  slideFromTop({ inView, element }: { inView: boolean, element: ElementRef }) {
    const anim = element.nativeElement

    if (inView) {
      const animation = this.animationBuilder.build([
        style({ transform: 'translateY(-100%)' }),
        animate('1s ease-out', style({ transform: 'translateY(0%)' }))
      ])

      // Check if the animation has already been played
      if (!anim.classList.contains('played')) {
        anim.classList.add('played') // Add a class to mark the element as played
        animation.create(anim).play()
      }
    }
  }
}